import type { RouteObject } from "react-router-dom";
import { lazy, Suspense } from "react";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";

const BookRitual = lazy(() => import("../pages/book-ritual/page"));
const Festivals = lazy(() => import("../pages/festivals/page"));
const FestivalDetail = lazy(() => import("../pages/festival-detail/page"));
const BookingConfirmation = lazy(() => import("../pages/booking-confirmation/page"));
const PrivacyPolicy = lazy(() => import("../pages/privacy-policy/page"));
const RitualDisclaimer = lazy(() => import("../pages/ritual-disclaimer/page"));
const RitualCategory = lazy(() => import("../pages/ritual-category/page"));
const Services = lazy(() => import("../pages/services/page"));
const RitualServices = lazy(() => import("../pages/ritual-services/page"));
const CorporateCeremonies = lazy(() => import("../pages/corporate-ceremonies/page"));
const ForNRIs = lazy(() => import("../pages/for-nris/page"));
const About = lazy(() => import("../pages/about/page"));
const Contact = lazy(() => import("../pages/contact/page"));
const Login = lazy(() => import("../pages/auth/login/page"));
const Register = lazy(() => import("../pages/auth/register/page"));
const MyBookings = lazy(() => import("../pages/my-bookings/page"));
const AdminDashboard = lazy(() => import("../pages/admin/dashboard/page"));
const Temples = lazy(() => import("../pages/temples/page"));
const FAQ = lazy(() => import("../pages/faq/page"));
const FestivalPotlis = lazy(() => import("../pages/festival-potlis/page"));
const FestivalPotliDetail = lazy(() => import("../pages/festival-potli-detail/page"));
const AnnualSubscription = lazy(() => import("../pages/annual-subscription/page"));
const RefundPolicy = lazy(() => import("../pages/refund-policy/page"));
const SubscriptionPolicy = lazy(() => import("../pages/subscription-policy/page"));
const CorporateRefundPolicy = lazy(() => import("../pages/corporate-refund-policy/page"));
const InternationalShipping = lazy(() => import("../pages/international-shipping/page"));
const Donations = lazy(() => import("../pages/donations/page"));
const PriestsPage = lazy(() => import("../pages/priests/page"));
const SpiritualMerchandise = lazy(() => import("../pages/spiritual-merchandise/page"));
const OnlineClasses = lazy(() => import("../pages/online-classes/page"));
const SeniorCitizenServices = lazy(() => import("../pages/senior-citizen-services/page"));

/**
 * Helper to wrap lazy‑loaded components with Suspense.
 * This adds a fallback UI and prevents the app from crashing
 * if a component fails to load.
 */
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <Suspense fallback={<div>Loading...</div>}>
    <Component />
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/services",
    element: withSuspense(Services),
  },
  {
    path: "/rituals",
    element: withSuspense(RitualServices),
  },
  {
    path: "/corporate-ceremonies",
    element: withSuspense(CorporateCeremonies),
  },
  {
    path: "/temples",
    element: withSuspense(Temples),
  },
  {
    path: "/for-nris",
    element: withSuspense(ForNRIs),
  },
  {
    path: "/about",
    element: withSuspense(About),
  },
  {
    path: "/contact",
    element: withSuspense(Contact),
  },
  {
    path: "/book-ritual",
    element: withSuspense(BookRitual),
  },
  {
    path: "/booking-confirmation",
    element: withSuspense(BookingConfirmation),
  },
  {
    path: "/festivals",
    element: withSuspense(Festivals),
  },
  {
    path: "/festival/:id",
    element: withSuspense(FestivalDetail),
  },
  {
    path: "/festival-potli/:festivalId",
    element: withSuspense(FestivalPotliDetail),
  },
  {
    path: "/ritual-category/:categoryId",
    element: withSuspense(RitualCategory),
  },
  {
    path: "/privacy-policy",
    element: withSuspense(PrivacyPolicy),
  },
  {
    path: "/ritual-disclaimer",
    element: withSuspense(RitualDisclaimer),
  },
  {
    path: "/auth/login",
    element: withSuspense(Login),
  },
  {
    path: "/auth/register",
    element: withSuspense(Register),
  },
  {
    path: "/my-bookings",
    element: withSuspense(MyBookings),
  },
  {
    path: "/admin/dashboard",
    element: withSuspense(AdminDashboard),
  },
  {
    path: "/faq",
    element: withSuspense(FAQ),
  },
  {
    path: "/festival-potlis",
    element: withSuspense(FestivalPotlis),
  },
  {
    path: "/annual-subscription",
    element: withSuspense(AnnualSubscription),
  },
  {
    path: "/refund-policy",
    element: withSuspense(RefundPolicy),
  },
  {
    path: "/subscription-policy",
    element: withSuspense(SubscriptionPolicy),
  },
  {
    path: "/corporate-refund-policy",
    element: withSuspense(CorporateRefundPolicy),
  },
  {
    path: "/international-shipping",
    element: withSuspense(InternationalShipping),
  },
  {
    path: "/donations",
    element: withSuspense(Donations),
  },
  {
    path: "/priests",
    element: withSuspense(PriestsPage),
  },
  {
    path: "/spiritual-merchandise",
    element: withSuspense(SpiritualMerchandise),
  },
  {
    path: "/online-classes",
    element: withSuspense(OnlineClasses),
  },
  {
    path: "/senior-citizen-services",
    element: withSuspense(SeniorCitizenServices),
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
